import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export type ReservationStatus =
  | 'REQUESTED'
  | 'ACTIVE'
  | 'DELETE_REQUESTED'
  | 'DELETING_UNKNOWN'
  | 'UNKNOWN'
  | 'FAILED'
  | 'CANCELLED';
export interface Reservation {
  id: string | null;
  unit_id: string;
  device_id: string;
  hotel_id: string | null;
  reservation_id: string | null;
  status: ReservationStatus | null;
  reserv_from: string | null;
  reserv_until: string | null;
  revision: number | null;
  last_result: string | null;
}
const record = (x: unknown): x is Record<string, unknown> =>
  !!x && typeof x === 'object' && !Array.isArray(x);
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '예약 응답을 확인할 수 없습니다.',
  });
function reservation(x: unknown): x is Reservation {
  if (
    !record(x) ||
    !isUuid(x.unit_id) ||
    !isUuid(x.device_id) ||
    !(x.hotel_id === null || typeof x.hotel_id === 'string')
  )
    return false;
  if (x.id === null)
    return (
      x.reservation_id === null &&
      x.status === null &&
      x.reserv_from === null &&
      x.reserv_until === null &&
      x.revision === null &&
      x.last_result === null
    );
  return (
    isUuid(x.id) &&
    typeof x.reservation_id === 'string' &&
    typeof x.status === 'string' &&
    [
      'REQUESTED',
      'ACTIVE',
      'DELETE_REQUESTED',
      'DELETING_UNKNOWN',
      'UNKNOWN',
      'FAILED',
      'CANCELLED',
    ].includes(x.status) &&
    isOffsetDateTime(x.reserv_from) &&
    isOffsetDateTime(x.reserv_until) &&
    Number.isSafeInteger(x.revision) &&
    Number(x.revision) > 0 &&
    (x.last_result === null || typeof x.last_result === 'string')
  );
}
export async function readReservations(
  client: ServiceClient,
  filters: { property_id?: string; unit_id?: string; device_id?: string; cursor?: string },
  signal?: AbortSignal,
) {
  const x = await client.get<unknown>('admin/access-reservations', {
    params: filters,
    signal,
    headers: { 'Cache-Control': 'no-store' },
  });
  if (
    !record(x) ||
    !Array.isArray(x.items) ||
    !x.items.every(reservation) ||
    !(x.next_cursor === null || isUuid(x.next_cursor))
  )
    throw invalid();
  return { items: x.items, next_cursor: x.next_cursor };
}
interface Command {
  command_id: string;
  expected_revision: number;
  id: string;
  unit_id?: string;
  device_id?: string;
  force?: boolean;
  reason?: string | null;
}
export async function reservationCommand(
  client: ServiceClient,
  action: 'create' | 'cancel' | 'reset',
  body: Command,
) {
  const { id, ...rest } = body;
  const path =
    action === 'create' ? 'admin/access-reservations' : `admin/access-reservations/${id}/${action}`;
  const x = await client.post<unknown>(path, { json: action === 'create' ? body : rest });
  if (!record(x) || x.id !== id || !Number.isSafeInteger(x.revision) || Number(x.revision) < 1)
    throw invalid();
  return x;
}
